import { batch, signal, useSignal, useSignalEffect, type Signal } from "@preact/signals";
import { useSignalRef } from "@preact/signals/utils";
import throttle from "nano-throttle";
import createPanZoom from "panzoom";
import { type ComponentChildren } from "preact";
import { type JSXInternal } from "preact/src/jsx";

export interface Vec2 {
	x: number,
	y: number,
}

export interface View {
	zoom: Signal<number>,
	box: Signal<{ min: Vec2, max: Vec2 }>,
}

export interface DrawerCtx {
	ctx: CanvasRenderingContext2D,
	view: View,
	scale: number,
	origin: Vec2,
	scaleX(x: number): number,
	scaleY(y: number): number,
	scaled(x: number, y: number, z?: number): [xs: number, ys: number],
	clear: () => void,
	forceRedraw: Signal<{clear: boolean}>,
}

export type CleanupFunction = () => void;
export type DrawFunction = (dCtx: DrawerCtx) => CleanupFunction;

interface RenderAllProps {
	fromM: number,
	toM: number,
	svgSize: number,
	draw: DrawFunction,
	children: undefined | {
		svgUnder: ComponentChildren,
		svgOver: ComponentChildren,
	},
}

export default function RenderAll({ fromM, toM, svgSize, draw, children = { svgOver: null, svgUnder: null } }: RenderAllProps) {
	const canvasRef = useSignalRef<HTMLCanvasElement | null>(null);
	const innerWrapRef = useSignalRef<HTMLDivElement | null>(null);
	const outerWrapRef = useSignalRef<HTMLDivElement | null>(null);
	const gridMinorRef = useSignalRef<HTMLDivElement | null>(null);
	const gridMajorRef = useSignalRef<HTMLDivElement | null>(null);

	const viewBox = useSignal("0 0 1000 1000");
	const svgStyle = useSignal<JSXInternal.CSSProperties>({});

	const forceRedraw = useSignal({clear: true});

	useSignalEffect(() => {
		if (!canvasRef.current || !innerWrapRef.current || !outerWrapRef.current || !gridMinorRef.current || !gridMajorRef.current) {
			return;
		}

		const _ctx = canvasRef.current.getContext("2d");
		if (!_ctx) {
			throw new Error("Failed to get canvas rendering context");
		}
		const ctx = _ctx;

		const pz = createPanZoom(innerWrapRef.current, {
			// @ts-expect-error smoothScroll also accepts settings object
			smoothScroll: { amplitude: 0.04, minVelocity: 10 },
			minZoom: 0.5,
			// transformOrigin: {x: 0.5, y: 0.5},
			zoomDoubleClickSpeed: 1, // disable default doubleclick action, instead use it to reset zoom/pan
			onDoubleClick() {
				pz.moveTo(0, 0);
				pz.zoomAbs(0, 0, 1);
				return true;
			},
		});

		const border = 300; // px boundary rendered around visible area

		const outer = outerWrapRef.current;
		const dpr = window.devicePixelRatio || 1;

		const defaultSize = toM - fromM;
		const w = outer.clientWidth;
		const h = outer.clientHeight;

		const isWider = w >= h;
		const smaller = Math.min(w, h);
		// default scale fits fromM / toM range in the smaller dimension
		const scale = smaller / defaultSize;

		const origin: Vec2 = { x: 0, y: 0 };

		// position of 0, 0 is in lower left corner

		if (isWider) {
			origin.x = ((w - h) / 4) / scale - fromM;
			origin.y = h / scale + fromM;
		} else {
			origin.x = -fromM;
			origin.y = w / scale + fromM;
		}

		batch(() => {
			svgStyle.value = {
				width: 2 * svgSize * scale,
				height: 2 * svgSize * scale,
				left: (-svgSize + origin.x) * scale,
				top: (-svgSize + origin.y) * scale,
			};
			viewBox.value = `-${svgSize}, -${svgSize}, ${2 * svgSize}, ${2 * svgSize}`;
		});

		// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
		const grid = gridMinorRef.current.parentElement!;
		grid.style.height = `${100 * scale}px`;
		grid.style.width = `${100 * scale}px`;
		grid.style.left = `-${50 * scale}px`;
		grid.style.top = `-${50 * scale}px`;

		gridMinorRef.current.style.backgroundSize = `${scale}px ${scale}px`;
		gridMajorRef.current.style.backgroundSize = `${scale}px ${scale}px`;

		gridMinorRef.current.style.backgroundPosition = `left ${origin.x * scale + scale / 2 - 1}px top ${origin.y * scale + scale / 2 - 1}px`;
		gridMajorRef.current.style.backgroundPosition = `left ${origin.x * scale - 1}px top ${origin.y * scale - 1}px`;

		const view: View = {
			zoom: signal(1),
			box: signal({
				min: { x: 0, y: 0 },
				max: { x: 0, y: 0 },
			}),
		};

		function update() {
			const o = pz.getTransform();

			const f = 1 / o.scale;
			const canvasMatrix = new DOMMatrix();
			canvasMatrix.translateSelf((-o.x - border) * f, (-o.y - border) * f);
			canvasMatrix.scaleSelf(f / dpr);

			ctx.canvas.style.transformOrigin = "0 0 0";

			ctx.canvas.style.transform = canvasMatrix.toString();

			ctx.setTransform(o.scale * dpr, 0, 0, o.scale * dpr, (o.x + border) * dpr, (o.y + border) * dpr);

			// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
			gridMinorRef.current!.style.opacity = o.scale >= 0.6 ? "1" : "0";

			const min = canvasMatrix.transformPoint();
			const max = canvasMatrix.transformPoint({ x: ctx.canvas.width, y: ctx.canvas.height });
			batch(() => {
				view.box.value = { min, max };
				if (o.scale != view.zoom.peek()) {
					view.zoom.value = o.scale;
				}
				forceRedraw.value = {clear: true};
			});
		}

		pz.on("pan", throttle(() => {
			requestAnimationFrame(update);
		}, 100));

		pz.on("zoom", throttle(() => {
			requestAnimationFrame(update);
		}, 100));

		const observer = new ResizeObserver(throttle(() => {
			requestAnimationFrame(() => {
				ctx.canvas.width = (outer.clientWidth + border * 2) * dpr;
				ctx.canvas.height = (outer.clientHeight + border * 2) * dpr;
				update();
			});
		}, 200));

		observer.observe(outerWrapRef.current);

		const disposeDrawer = draw({
			ctx,
			view,
			scale,
			origin,
			scaleX(this: DrawerCtx, x) {
				return this.scale * (this.origin.x + x);
			},
			scaleY(this: DrawerCtx, y) {
				return this.scale * (this.origin.y - y);
			},
			scaled(this: DrawerCtx, x, y) {
				return [this.scaleX(x), this.scaleY(y)];
			},
			clear() {
				ctx.save();
				ctx.setTransform(1, 0, 0, 1, 0, 0);
				ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
				ctx.restore();
			},
			forceRedraw,
		});
		
		return () => { // cleanup
			observer.disconnect();
			pz.dispose();
			disposeDrawer();
		};
	});

	return (
		<div class="canvas-wrapper-outer" ref={outerWrapRef}>
			<div class="canvas-wrapper-inner" ref={innerWrapRef}>
				<div class="grid">
					<div class="grid-minor" ref={gridMinorRef} />
					<div class="grid-major" ref={gridMajorRef} />
				</div>
				<svg viewBox={viewBox} style={svgStyle.value} strokeWidth={0.01} fill="transparent">
					{children.svgUnder}
				</svg>
				<canvas ref={canvasRef} />
				{/* <svg viewBox={viewBox} style={svgStyle.value} strokeWidth={0.01} fill="transparent">
					{children.svgOver}
				</svg> */}
			</div>
		</div>
	);
}
